/**
 * The normal and specular maps a CanvasTexture gives a textured canvas item. Godot samples them raw
 * with the item's sampler, at the diffuse texture's uv (`canvas.glsl:654,679`), so each takes the
 * same sampler and no window of its own: the item reads them through the diffuse map's uv.
 */
import { useLayoutEffect, useMemo } from 'react';
import { useTexture2D } from '../resources/useTexture2D';
import { useAtlasTextureCrop } from '../resources/useAtlasTextureCrop';
import { atlasTextureOnSheet } from '../resources/textures/atlastexture/decode';
import { imageSize } from '../resources/formats/image/imagePixels';
import { canvasTextureSpecular } from '../resources/textures/canvastexture/decode';
import { useCanvas2DTexture } from './canvas2DTextureDecode';
/**
 * Hands `onSurface` the maps of `slot`, and null while its normal map has not resolved or on unmount.
 * An item mounts it only while its CanvasTexture names a normal map, without which Godot shades none,
 * so an item without one runs none of the texture hooks.
 */
export function CanvasItemSurfaceSource({ slot, sampler, onSurface, }) {
    const surface = useCanvasItemSurface(slot, sampler);
    useLayoutEffect(() => {
        onSurface(surface);
        return () => onSurface(null);
    }, [surface, onSurface]);
    return null;
}
function useCanvasItemSurface(slot, sampler) {
    const { texture, externalResources, internalResources } = slot;
    const normal = useTexture2D(texture.normalTexture ?? undefined, externalResources, internalResources);
    const specular = useTexture2D(texture.specularTexture ?? undefined, externalResources, internalResources);
    const normalCell = useMapCell(unnested(normal), slot.atlas, internalResources);
    const specularCell = useMapCell(unnested(specular), slot.atlas, internalResources);
    // `useCanvas2DTexture` tags the view NoColorSpace: Godot reads both maps' raw texels
    // (`texture_storage.cpp:766,774`).
    const normalMap = useCanvas2DTexture(normalCell, sampler);
    const specularMap = useCanvas2DTexture(specularCell, sampler);
    return useMemo(() => (normalMap ? { normalMap, specularMap, specular: canvasTextureSpecular(texture) } : null), [normalMap, specularMap, texture]);
}
/** A map's texture, or null for a CanvasTexture, which each map setter refuses (`canvas_item.cpp:1801,1815`). */
function unnested(map) {
    return map.canvasTexture ? null : map.texture;
}
/** `map`, or the cell of it that `atlas` cuts while an AtlasTexture draws a cell of the diffuse. */
function useMapCell(map, atlas, internalResources) {
    const mapSize = imageSize(map?.image);
    const mapWidth = mapSize?.width;
    const mapHeight = mapSize?.height;
    const reference = useMemo(() => {
        if (!atlas || mapWidth === undefined || mapHeight === undefined)
            return null;
        const { reference: diffuse, sheetSize } = atlas;
        const texture = atlasTextureOnSheet(diffuse.texture, sheetSize, { width: mapWidth, height: mapHeight });
        return texture === diffuse.texture ? diffuse : { id: diffuse.id, texture };
    }, [atlas, mapWidth, mapHeight]);
    const cell = useAtlasTextureCrop(reference, map, internalResources);
    return atlas ? cell : map;
}
//# sourceMappingURL=canvasItemSurface.js.map